import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, of } from "rxjs";
import { ApiService } from "../core/api.service";
import { clarityRecordingUrl } from "../core/clarity.util";
import { DraftSummary } from "../core/models";

@Component({
  selector: "app-drafts-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule],
  templateUrl: "./drafts-page.component.html",
  styleUrls: ["./drafts-page.component.scss"],
})
export class DraftsPageComponent {
  private readonly api = inject(ApiService);
  private readonly refresh = signal(0);

  private readonly resource = rxResource({
    stream: () => {
      this.refresh();
      return this.api.getDrafts().pipe(
        catchError((err) => {
          console.error("Failed to load drafts:", err);
          return of([] as DraftSummary[]);
        }),
      );
    },
  });

  readonly loading = this.resource.isLoading;
  readonly drafts = computed(() => this.resource.value() ?? []);

  reload() {
    this.refresh.update((n) => n + 1);
  }

  shortOwner(ownerId: string): string {
    const raw = ownerId.replace(/^guest_/, "");
    return raw.length > 14 ? `${raw.slice(0, 8)}…${raw.slice(-4)}` : raw;
  }

  recordingUrl(draft: DraftSummary): string {
    return clarityRecordingUrl(draft.claritySessionId);
  }

  openRecording(event: Event, draft: DraftSummary) {
    event.stopPropagation();
    event.preventDefault();
    const url = this.recordingUrl(draft);
    if (url) window.open(url, "_blank", "noopener");
  }

  money(value: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  }

  dateLabel(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
}
